import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type {
  ExternalClubDto,
  OrganizationSettingsResponse,
  SeasonResponse,
  TeamResponse,
  TeamSyncSummaryDto,
  TurnierDeStatusDto,
} from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { OrgTabs } from '../components/OrgTabs';

const DBV_NRW = 'D6F7A756-39F3-4CB4-8F27-0E30E0421F4A';

interface MatchSyncSummary {
  teamsProcessed: number;
  created: number;
  updated: number;
}

type StepState = 'done' | 'active' | 'pending';

function stateStyles(state: StepState) {
  if (state === 'done') return 'bg-green-100 text-green-700';
  if (state === 'active') return 'bg-brand-accent text-white';
  return 'bg-slate-100 text-slate-400';
}

function StepTracker({
  labels,
  states,
  onJump,
}: {
  labels: string[];
  states: StepState[];
  onJump: (n: number) => void;
}) {
  return (
    <div className="flex items-start">
      {labels.map((label, i) => {
        const n = i + 1;
        return (
          <div key={label} className="flex flex-1 items-center last:flex-none">
            <button
              type="button"
              onClick={() => onJump(n)}
              className="flex flex-col items-center gap-1.5"
            >
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${stateStyles(states[i]!)}`}
              >
                {states[i] === 'done' ? '✓' : n}
              </span>
              <span
                className={`max-w-24 text-center text-xs ${states[i] === 'pending' ? 'text-slate-400' : 'font-medium text-slate-700'}`}
              >
                {label}
              </span>
            </button>
            {n < labels.length && (
              <div
                className={`mx-2 mb-5 h-0.5 flex-1 ${states[i] === 'done' ? 'bg-green-300' : 'bg-slate-200'}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepCard({
  n,
  label,
  subtitle,
  state,
  isOpen,
  onToggle,
  children,
}: {
  n: number;
  label: string;
  subtitle?: string;
  state: StepState;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <Card>
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-3 text-left">
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${stateStyles(state)}`}
        >
          {state === 'done' ? '✓' : n}
        </span>
        <span className="flex-1">
          <span className="block text-sm font-semibold text-slate-800">{label}</span>
          {subtitle && <span className="block text-xs text-slate-500">{subtitle}</span>}
        </span>
        <span className="text-slate-400">{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && <div className="mt-4 border-t border-slate-100 pt-4">{children}</div>}
    </Card>
  );
}

export function TurnierDePage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [federationId, setFederationId] = useState(DBV_NRW);
  const [q, setQ] = useState('');
  const [clubs, setClubs] = useState<ExternalClubDto[] | null>(null);
  const [summary, setSummary] = useState<TeamSyncSummaryDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSyncStatus, setShowSyncStatus] = useState(false);
  const [seasonId, setSeasonId] = useState('');
  const [matchSummary, setMatchSummary] = useState<MatchSyncSummary | null>(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [openStep, setOpenStep] = useState(1);

  const { data: status } = useQuery({
    queryKey: ['turnier-de', 'status'],
    queryFn: () => api<TurnierDeStatusDto>('/org/turnier-de/status', { orgScoped: true }),
  });

  const { data: settings } = useQuery({
    queryKey: ['org', 'settings'],
    queryFn: () => api<OrganizationSettingsResponse>('/org/settings', { orgScoped: true }),
  });
  const [intervalEnabled, setIntervalEnabled] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  useEffect(() => {
    if (settings) {
      setIntervalEnabled(settings.turnierDeSyncIntervalMinutes != null);
      setIntervalMinutes(settings.turnierDeSyncIntervalMinutes ?? 60);
    }
  }, [settings]);

  const { data: seasons } = useQuery({
    queryKey: ['seasons'],
    queryFn: () => api<SeasonResponse[]>('/season/seasons', { orgScoped: true }),
  });
  useEffect(() => {
    if (!seasons || seasons.length === 0 || seasonId) return;
    setSeasonId((seasons.find((s) => s.status === 'active') ?? seasons[0])!.id);
  }, [seasons, seasonId]);
  const season = seasons?.find((s) => s.id === seasonId);

  const { data: allTeams } = useQuery({
    queryKey: ['teams', 'all'],
    queryFn: () => api<{ data: TeamResponse[] }>('/teams?pageSize=200', { orgScoped: true }),
  });

  const step1Done = !!status?.connected;
  const step2Done = !!seasonId;
  const step3Done = summary !== null || (status?.teamsBySource.turnier_de ?? 0) > 0;
  const step4Done = (season?.teams.length ?? 0) > 0;
  const stepStates: StepState[] = [step1Done, step2Done, step3Done, step4Done].map((done, i) =>
    openStep === i + 1 ? 'active' : done ? 'done' : 'pending',
  );

  // Land the guide on the first unfinished step, once, when the page first loads
  // with data — after that, the user drives navigation via the step headers.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current || status === undefined || seasons === undefined) return;
    autoOpened.current = true;
    if (!step1Done) setOpenStep(1);
    else if (seasons.length === 0) setOpenStep(2);
    else if (!step3Done) setOpenStep(3);
    else setOpenStep(4);
  }, [status, seasons]);

  const search = useMutation({
    mutationFn: () =>
      api<ExternalClubDto[]>(
        `/org/turnier-de/clubs?federationId=${encodeURIComponent(federationId)}&q=${encodeURIComponent(q)}`,
        { orgScoped: true },
      ),
    onSuccess: (data) => {
      setClubs(data);
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Fehler bei der Vereinssuche'),
  });

  const connect = useMutation({
    mutationFn: (club: ExternalClubDto) =>
      api('/org/turnier-de/connect', {
        method: 'POST',
        orgScoped: true,
        body: { federationId, externalClubId: club.externalId, externalClubUrl: club.url, clubName: club.name },
      }),
    onSuccess: () => {
      setClubs(null);
      setError(null);
      setOpenStep(2);
      void qc.invalidateQueries({ queryKey: ['turnier-de'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Verbinden fehlgeschlagen'),
  });

  const sync = useMutation({
    mutationFn: () =>
      api<{ mode: string; summary: TeamSyncSummaryDto }>('/org/turnier-de/sync-teams?inline=true', {
        method: 'POST',
        orgScoped: true,
      }),
    onSuccess: (r) => {
      setSummary(r.summary);
      setOpenStep(4);
      void qc.invalidateQueries({ queryKey: ['turnier-de'] });
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Sync fehlgeschlagen'),
  });

  const syncMatches = useMutation({
    // `?inline=true`: this step sits in a guided, one-time setup wizard right
    // after connecting a club — same tradeoff as the team-sync button above,
    // a synchronous wait is simpler here than wiring up the polling
    // `SeasonPage.tsx`'s regular "Von Turnier.de aktualisieren" button uses
    // for potentially minutes-long, unattended background syncs (see
    // docs/turnier-de.md).
    mutationFn: () =>
      api<{ mode: 'inline'; summary: MatchSyncSummary }>(`/season/seasons/${seasonId}/sync-matches?inline=true`, {
        method: 'POST',
        orgScoped: true,
      }),
    onSuccess: (r) => {
      setMatchSummary(r.summary);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['season'] });
      void qc.invalidateQueries({ queryKey: ['seasons'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Spiel-Sync fehlgeschlagen'),
  });

  const [intervalError, setIntervalError] = useState<string | null>(null);
  const [intervalSaved, setIntervalSaved] = useState(false);
  const saveInterval = useMutation({
    mutationFn: (minutes: number | null) =>
      api('/org/turnier-de/sync-interval', { method: 'PATCH', orgScoped: true, body: { minutes } }),
    onSuccess: () => {
      setIntervalError(null);
      setIntervalSaved(true);
      setTimeout(() => setIntervalSaved(false), 2000);
      void qc.invalidateQueries({ queryKey: ['org', 'settings'] });
    },
    onError: (e) =>
      setIntervalError(e instanceof ApiError ? e.message : 'Intervall konnte nicht gespeichert werden.'),
  });

  const createTeam = useMutation({
    mutationFn: () => api('/teams', { method: 'POST', orgScoped: true, body: { name: newTeamName } }),
    onSuccess: () => {
      setNewTeamName('');
      setError(null);
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Anlegen fehlgeschlagen'),
  });

  const addSeasonTeam = useMutation({
    mutationFn: (teamId: string) =>
      api(`/season/seasons/${seasonId}/teams`, { method: 'POST', orgScoped: true, body: { teamId } }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['seasons'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Zuordnen fehlgeschlagen'),
  });
  const removeSeasonTeam = useMutation({
    mutationFn: (teamId: string) =>
      api(`/season/seasons/${seasonId}/teams/${teamId}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['seasons'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Entfernen fehlgeschlagen'),
  });
  const deleteTeam = useMutation({
    mutationFn: (teamId: string) => api(`/teams/${teamId}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams'] });
      void qc.invalidateQueries({ queryKey: ['seasons'] });
      void qc.invalidateQueries({ queryKey: ['season'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Löschen fehlgeschlagen'),
  });

  return (
    <div className="space-y-4">
      <h1 className="hidden text-xl font-semibold md:block">Verein</h1>
      <OrgTabs />

      <Card>
        <StepTracker
          labels={['Verein suchen', 'Saison auswählen', 'Mannschaften synchronisieren', 'Der Saison zuordnen']}
          states={stepStates}
          onJump={setOpenStep}
        />
      </Card>

      {error && <Alert>{error}</Alert>}

      <StepCard
        n={1}
        label="Verein suchen"
        subtitle={
          status?.connected
            ? `Verbunden — Verein ${status.externalClubId}`
            : 'Verein bei Turnier.de suchen und verbinden'
        }
        state={stepStates[0]!}
        isOpen={openStep === 1}
        onToggle={() => setOpenStep(openStep === 1 ? 0 : 1)}
      >
        {status?.connected && (
          <div className="mb-4 space-y-1 text-sm">
            <p>
              Verbunden — Verband <code>{status.federationId}</code>, Verein{' '}
              <code>{status.externalClubId}</code>
              {status.externalClubUrl && (
                <>
                  {' · '}
                  <a
                    className="text-brand-accent"
                    href={status.externalClubUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Vereinsseite
                  </a>
                </>
              )}
            </p>
            <p className="text-slate-500">Unten kann bei Bedarf ein anderer Verein verbunden werden.</p>
          </div>
        )}
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            search.mutate();
          }}
        >
          <Field label="Verband-ID (Turnier.de)">
            <Input value={federationId} onChange={(e) => setFederationId(e.target.value)} className="w-80" />
          </Field>
          <Field label="Vereinsname">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="z. B. St. Tönis" />
          </Field>
          <Button type="submit" disabled={search.isPending}>
            Suchen
          </Button>
        </form>

        {clubs && (
          <ul className="mt-4 divide-y divide-slate-100">
            {clubs.slice(0, 50).map((c) => (
              <li key={c.externalId} className="flex items-center justify-between py-2 text-sm">
                <span>
                  {c.name} <span className="text-slate-400">{c.clubNumber}</span>{' '}
                  {c.teamCount != null && <span className="text-slate-400">· {c.teamCount} Mannschaften</span>}
                </span>
                <Button variant="ghost" onClick={() => connect.mutate(c)} disabled={connect.isPending}>
                  Verbinden
                </Button>
              </li>
            ))}
            {clubs.length === 0 && <li className="py-2 text-sm text-slate-500">Keine Treffer.</li>}
          </ul>
        )}
      </StepCard>

      <StepCard
        n={2}
        label="Saison auswählen"
        subtitle={season ? `Ausgewählt: ${season.name}` : 'Für welche Saison die Mannschaften gelten sollen'}
        state={stepStates[1]!}
        isOpen={openStep === 2}
        onToggle={() => setOpenStep(openStep === 2 ? 0 : 2)}
      >
        {seasons && seasons.length > 0 ? (
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Saison">
              <select
                className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={seasonId}
                onChange={(e) => setSeasonId(e.target.value)}
              >
                {seasons.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
            <Button variant="ghost" onClick={() => setOpenStep(3)}>
              Weiter
            </Button>
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            Noch keine Saison angelegt.{' '}
            <Link to="/season" className="text-brand-accent">
              Auf der Saison-Seite
            </Link>{' '}
            zuerst eine anlegen.
          </p>
        )}
      </StepCard>

      <StepCard
        n={3}
        label="Mannschaften synchronisieren"
        subtitle={
          status?.connected
            ? `${status.teamsBySource.turnier_de} aus Turnier.de, ${status.teamsBySource.manual} manuell`
            : 'Erst Verein verbinden (Schritt 1)'
        }
        state={stepStates[2]!}
        isOpen={openStep === 3}
        onToggle={() => setOpenStep(openStep === 3 ? 0 : 3)}
      >
        {status?.connected ? (
          <>
            <Button onClick={() => sync.mutate()} disabled={sync.isPending}>
              Mannschaften synchronisieren
            </Button>
            {summary && (
              <Alert kind="success">
                {summary.created} neu · {summary.adopted} übernommen · {summary.updated} aktualisiert ·{' '}
                {summary.stale} nicht mehr im Verband
              </Alert>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500">Dazu muss zuerst in Schritt 1 ein Verein verbunden werden.</p>
        )}
      </StepCard>

      <StepCard
        n={4}
        label="Der Saison zuordnen"
        subtitle={season ? `${season.teams.length} Mannschaft(en) in „${season.name}"` : undefined}
        state={stepStates[3]!}
        isOpen={openStep === 4}
        onToggle={() => setOpenStep(openStep === 4 ? 0 : 4)}
      >
        {seasons && seasons.length > 0 ? (
          <>
            <ul className="divide-y divide-slate-100">
              {allTeams?.data.map((t) => {
                const inSeason = season?.teams.some((st) => st.teamId === t.id) ?? false;
                return (
                  <li key={t.id} className="flex items-center justify-between py-2 text-sm">
                    <span>
                      {t.name}
                      {t.externalTeamId ? (
                        <span className="ml-2 text-xs text-slate-400">Turnier.de</span>
                      ) : (
                        <span className="ml-2 text-xs text-slate-400">manuell</span>
                      )}
                      {' · '}
                      {t.memberCount} Spieler
                    </span>
                    <div className="flex items-center gap-3">
                      {can('season.edit') && (
                        <label className="flex items-center gap-1.5 text-xs text-slate-500">
                          <input
                            type="checkbox"
                            checked={inSeason}
                            onChange={() =>
                              inSeason ? removeSeasonTeam.mutate(t.id) : addSeasonTeam.mutate(t.id)
                            }
                          />
                          in dieser Saison
                        </label>
                      )}
                      {can('teams.delete') && (
                        <Button
                          variant="ghost"
                          className="px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                          disabled={deleteTeam.isPending}
                          onClick={() => {
                            if (
                              confirm(
                                `„${t.name}" wirklich löschen? Die Mannschaft wird aus allen Saisons entfernt, ihre Spiele werden gelöscht.`,
                              )
                            ) {
                              deleteTeam.mutate(t.id);
                            }
                          }}
                        >
                          Löschen
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
              {allTeams?.data.length === 0 && (
                <li className="py-2 text-sm text-slate-500">Noch keine Mannschaften.</li>
              )}
            </ul>

            {can('teams.create') && (
              <form
                className="mt-3 flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (newTeamName) createTeam.mutate();
                }}
              >
                <Input
                  placeholder="Neue Mannschaft (Name)"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                />
                <Button type="submit" variant="ghost" disabled={!newTeamName || createTeam.isPending}>
                  Anlegen
                </Button>
              </form>
            )}

            {can('season.edit') && can('teams.sync') && (
              <div className="mt-4 border-t border-slate-100 pt-4">
                <p className="mb-2 text-sm text-slate-500">
                  Saisonspiele aller Mannschaften dieser Saison von Turnier.de laden:
                </p>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setMatchSummary(null);
                    syncMatches.mutate();
                  }}
                  disabled={!seasonId || syncMatches.isPending}
                >
                  {syncMatches.isPending ? 'Wird synchronisiert …' : 'Spiele synchronisieren'}
                </Button>
                {syncMatches.isPending && (
                  <p className="mt-2 text-xs text-slate-400">
                    Das kann durch das Rate-Limit von Turnier.de ein bis mehrere Minuten dauern.
                  </p>
                )}
                {matchSummary && (
                  <Alert kind="success">
                    Erfolgreich synchronisiert: {matchSummary.teamsProcessed} Mannschaften geprüft ·{' '}
                    {matchSummary.created} Spiele neu · {matchSummary.updated} aktualisiert
                  </Alert>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500">
            Dazu muss zuerst in Schritt 2 eine Saison ausgewählt werden können.
          </p>
        )}
      </StepCard>

      {step1Done && can('season.edit') && can('teams.sync') && (
        <Card title="Automatischer Sync">
          {intervalSaved && <Alert kind="success">Gespeichert.</Alert>}
          {intervalError && <Alert>{intervalError}</Alert>}
          <p className="mb-3 text-sm text-slate-500">
            Saisonspiele aller Mannschaften und Saisons regelmäßig im Hintergrund von Turnier.de
            aktualisieren — bestehende Rückmeldungen (Anmeldungen/Absagen) bleiben dabei erhalten.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={intervalEnabled}
                onChange={(e) => {
                  setIntervalEnabled(e.target.checked);
                  saveInterval.mutate(e.target.checked ? intervalMinutes : null);
                }}
              />
              Automatisch synchronisieren
            </label>
            {intervalEnabled && (
              <>
                <Input
                  type="number"
                  min={60}
                  max={10_080}
                  step={30}
                  className="w-24"
                  value={intervalMinutes}
                  onChange={(e) => setIntervalMinutes(Number(e.target.value))}
                  onBlur={() => {
                    const clamped = Math.min(10_080, Math.max(60, intervalMinutes));
                    setIntervalMinutes(clamped);
                    saveInterval.mutate(clamped);
                  }}
                />
                <span className="text-sm text-slate-500">Minuten (mind. 60)</span>
              </>
            )}
          </div>
        </Card>
      )}

      {status && status.entities.length > 0 && (
        <Card>
          <button
            type="button"
            onClick={() => setShowSyncStatus((s) => !s)}
            className="flex w-full items-center justify-between text-left text-base font-semibold text-slate-800"
          >
            <span>Sync-Status ({status.entities.length})</span>
            <span className="text-sm font-normal text-slate-400">{showSyncStatus ? '▲' : '▼'}</span>
          </button>
          {showSyncStatus && (
            <table className="mt-3 w-full text-xs">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-1">Typ</th>
                  <th>Externe ID</th>
                  <th>Status</th>
                  <th>Zuletzt erfolgreich</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {status.entities.map((e) => (
                  <tr key={`${e.entityType}-${e.externalId}`}>
                    <td className="py-1">{e.entityType}</td>
                    <td>{e.externalId}</td>
                    <td className={e.syncStatus === 'stale' ? 'text-amber-600' : e.syncStatus === 'error' ? 'text-red-600' : ''}>
                      {e.syncStatus}
                    </td>
                    <td>{e.lastSuccessAt ? new Date(e.lastSuccessAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  );
}
